import {
  PublicClientApplication,
  Configuration,
  AccountInfo,
  AuthenticationResult,
  LogLevel,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { AuthService, ClientConfiguration, TokenRefreshError } from '../types/AuthTypes';

/**
 * EntraAuthService
 * 
 * Handles Azure AD (Entra) authentication for impersonation with:
 * - Popup-based flows (never redirects)
 * - organizationToken management (24h lifetime)
 */
export class EntraAuthService implements AuthService {
  private msalInstance: PublicClientApplication | null = null;
  private config: ClientConfiguration | null = null;

  async initialize(config: ClientConfiguration): Promise<void> {
    this.config = config;

    const msalConfig: Configuration = {
      auth: {
        clientId: config.adClientId,
        authority: config.authority,
        redirectUri: `${window.location.origin}/`,
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: false,
      },
      system: {
        allowNativeBroker: false,
        allowRedirectInIframe: false, // Prevent any redirects in iframe
        navigateFrameWait: 0, // Disable iframe-based silent auth in sandboxed iframes
        loggerOptions: {
          loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
            if (containsPii) return;
            
            switch (level) {
              case LogLevel.Error:
                console.error('[EntraAuth]', message);
                break;
              case LogLevel.Warning:
                console.warn('[EntraAuth]', message);
                break;
              case LogLevel.Info:
                console.info('[EntraAuth]', message);
                break;
              case LogLevel.Verbose:
                console.debug('[EntraAuth]', message);
                break;
            }
          },
        },
      },
    };

    this.msalInstance = new PublicClientApplication(msalConfig);
    await this.msalInstance.initialize();
  }

  /**
   * Login with hints from Admin.Web iframe context
   */
  async loginWithHints(userName: string, sid: string, orgToken: string): Promise<AuthenticationResult> {
    if (!this.msalInstance || !this.config) {
      throw new Error('EntraAuthService not initialized');
    }

    // Helper to parse string arrays like "['a', 'b']" to ['a', 'b']
    const parseStringArray = (str: string): string[] => {
      if (Array.isArray(str)) return str;
      if (typeof str === 'string') {
        try {
          // Remove surrounding quotes and split
          const cleaned = str.replace(/^['"]|['"]$/g, '').replace(/^\[|\]$/g, '');
          return cleaned.split(',').map(s => s.trim().replace(/^['"]|['"]$/g, ''));
        } catch {
          return [];
        }
      }
      return [];
    };

    // Store organization token with 24h expiry
    sessionStorage.setItem('organizationToken', orgToken);
    sessionStorage.setItem('organizationTokenExpiry', (Date.now() + 24 * 60 * 60 * 1000).toString());

    const adScopes = parseStringArray(this.config.adScopes);
    
    // Step 1: Check if we already have an account in cache (shared from Admin.Web)
    const accounts = this.msalInstance.getAllAccounts();
    const matchingAccount = accounts.find(acc => 
      acc.username?.toLowerCase() === userName?.toLowerCase()
    );

    // Step 2: If account exists, try silent token acquisition (fastest path for SSO)
    if (matchingAccount) {
      console.info('[EntraAuth] Found existing account in cache, attempting silent token acquisition for SSO');
      try {
        const result = await this.msalInstance.acquireTokenSilent({
          scopes: adScopes,
          account: matchingAccount,
          forceRefresh: false, // Use cached token if valid
        });
        console.info('[EntraAuth] Silent SSO successful using cached account');
        return result;
      } catch (silentError: any) {
        console.warn('[EntraAuth] Silent token acquisition failed, will try ssoSilent:', silentError.errorCode);
        // Fall through to ssoSilent
      }
    } else {
      console.info('[EntraAuth] No matching account in cache');
    }

    // Step 3: Try ssoSilent with login hints (iframe-safe configuration)
    const loginRequest = {
      scopes: [...adScopes],
      loginHint: userName,
      sid,
      prompt: 'none' as const,
    };

    try {
      console.info('[EntraAuth] Attempting ssoSilent with login hints');
      return await this.msalInstance.ssoSilent(loginRequest);
    } catch (error: any) {
      // Check for iframe-specific navigation errors (sandboxed iframe blocking navigation)
      const isNavigationBlocked = error.message?.includes('Unsafe attempt to initiate navigation') ||
                                  error.message?.includes('sandboxed') ||
                                  error.errorCode === 'monitor_window_timeout';
      
      const isInteractionRequired = error instanceof InteractionRequiredAuthError;
      const isSessionNotExist = error.errorCode === 'invalid_grant' || 
                               error.message?.includes('160021') ||
                               error.message?.includes('user session which does not exist');
      
      if (isNavigationBlocked || isInteractionRequired || isSessionNotExist) {
        // Navigation blocked by iframe sandbox, interaction required, or no session - use popup
        console.info('[EntraAuth] ssoSilent failed (navigation blocked/interaction/session), using popup:', error.errorCode || error.message);
        try {
          // Ensure no interaction is in progress
          if (this.msalInstance.getActiveAccount()) {
            console.info('[EntraAuth] Clearing active account to prevent interaction conflicts');
          }          
          const popupRequest = {
            scopes: [...adScopes],
            loginHint: userName,
            sid,
            // Remove prompt for popup to allow user interaction
          };
          return await this.msalInstance.loginPopup(popupRequest);
        } catch (popupError: any) {
          console.error('[EntraAuth] Login popup failed:', popupError);
          
          // Handle interaction in progress error
          if (popupError.errorCode === 'interaction_in_progress') {
            throw new TokenRefreshError('Another authentication is in progress. Please wait and try again.', popupError);
          }
          
          throw new TokenRefreshError('Unable to complete authentication. Please try again.', popupError);
        }
      }
      throw error;
    }
  }

  /**
   * Acquire token silently, with popup fallback
   */
  async acquireToken(account: AccountInfo): Promise<string> {
    if (!this.msalInstance || !this.config) {
      throw new Error('EntraAuthService not initialized');
    }

    // Helper to parse string arrays like "['a', 'b']" to ['a', 'b']
    const parseStringArray = (str: string): string[] => {
      if (Array.isArray(str)) return str;
      if (typeof str === 'string') {
        try {
          // Remove surrounding quotes and split
          const cleaned = str.replace(/^['"]|['"]$/g, '').replace(/^\[|\]$/g, '');
          return cleaned.split(',').map(s => s.trim().replace(/^['"]|['"]$/g, ''));
        } catch {
          return [];
        }
      }
      return [];
    };

    const adScopes = parseStringArray(this.config.adScopes);
    
    try {
      const result = await this.msalInstance.acquireTokenSilent({
        scopes: adScopes,
        account,
      });
      return result.accessToken;
    } catch (error: any) {
      // Check for iframe-related errors or interaction required
      const isIframeError = error.errorCode === 'monitor_window_timeout' || 
                           error.message?.includes('iframe') ||
                           error.message?.includes('sandbox');
      const isInteractionRequired = error instanceof InteractionRequiredAuthError;
      
      if (isIframeError || isInteractionRequired) {
        // Try popup for token refresh, but handle interaction conflicts
        console.warn('[EntraAuth] Silent token refresh failed (iframe/sandboxed), using popup');
        try {
          const result = await this.msalInstance.acquireTokenPopup({
            scopes: adScopes,
            account,
          });
          return result.accessToken;
        } catch (popupError: any) {
          console.error('[EntraAuth] Token refresh popup failed:', popupError);
          
          // Handle interaction in progress error specifically
          if (popupError.errorCode === 'interaction_in_progress') {
            throw new TokenRefreshError('Another authentication is in progress. Please wait and try again.', popupError);
          }
          
          // Throw specific error for UI to catch
          throw new TokenRefreshError('Unable to refresh authentication. Please reload.', popupError);
        }
      }
      throw error;
    }
  }

  /**
   * Logout - clear organization token and notify parent, but preserve Entra session
   */
  async logout(): Promise<void> {
    if (!this.msalInstance || !this.config) {
      throw new Error('EntraAuthService not initialized');
    }

    // Clear organization token
    sessionStorage.removeItem('organizationToken');
    sessionStorage.removeItem('organizationTokenExpiry');

    // DO NOT clear MSAL cache - preserve the Entra session for Admin.Web
    // The user should remain authenticated in the parent application

    // Notify parent window if in iframe
    if (window.self !== window.top) {
      try {
        const parentOrigin = this.config.admin360Url.replace(/^(https?:\/\/[^/]+).*$/, '$1');
        window.parent.postMessage({ type: 'logout-complete' }, parentOrigin);
        console.info('[EntraAuth] Logout notification sent to parent (Entra session preserved)');
      } catch (error) {
        console.error('[EntraAuth] Failed to notify parent of logout:', error);
      }
    } else {
      console.info('[EntraAuth] Logout complete (Entra session preserved)');
    }
  }

  /**
   * Get all cached accounts
   */
  getAllAccounts(): AccountInfo[] {
    if (!this.msalInstance) {
      return [];
    }
    return this.msalInstance.getAllAccounts();
  }

  /**
   * Check if organization token is still valid
   */
  isOrgTokenValid(): boolean {
    const expiry = sessionStorage.getItem('organizationTokenExpiry');
    if (!expiry) return false;
    return Date.now() < parseInt(expiry, 10);
  }

  /**
   * Get the MSAL instance (for advanced use cases)
   */
  getInstance(): PublicClientApplication | null {
    return this.msalInstance;
  }
}
